// Scope-stacked shortcut registry (Doc 06 §8).
//
// The registry lives in the ViewModel layer — no React, no DOM — so it can be tested
// headlessly and shared between web and future mobile (hardware keyboard) bindings.
// Components register handlers by scope and call `dispatch` with a normalised key string.
// Higher scopes take priority: `dialog` bindings shadow `editor` bindings while active,
// and editor bindings are NOT invoked when the dialog scope is on top.
export const createShortcutRegistry = () => {
    // Per-scope merged handler maps. Rebuilt from registrations on each register/unregister.
    const handlersByScope = new Map();
    // Per-scope ordered list of registered binding maps (ordered so later registrations win).
    const registrationsByScope = new Map();
    // Active scope stack — "editor" is always the base.
    const scopeStack = ["editor"];
    const rebuildScope = (scope) => {
        const regs = registrationsByScope.get(scope) ?? [];
        const merged = new Map();
        for (const reg of regs) {
            for (const [key, handler] of Object.entries(reg)) {
                if (handler !== undefined)
                    merged.set(key, handler);
            }
        }
        handlersByScope.set(scope, merged);
    };
    return {
        register(scope, bindings) {
            const regs = registrationsByScope.get(scope) ?? [];
            regs.push(bindings);
            registrationsByScope.set(scope, regs);
            rebuildScope(scope);
            return () => {
                const list = registrationsByScope.get(scope) ?? [];
                const idx = list.indexOf(bindings);
                if (idx !== -1)
                    list.splice(idx, 1);
                rebuildScope(scope);
            };
        },
        dispatch(key) {
            // Only the top scope on the stack is active. If the dialog scope is on top,
            // editor bindings are fully suppressed — even when the dialog doesn't handle the key.
            // This prevents modal-open shortcuts (Esc, etc.) from leaking into the editor layer.
            const top = scopeStack[scopeStack.length - 1] ?? "editor";
            const handler = handlersByScope.get(top)?.get(key);
            if (handler !== undefined) {
                handler();
                return true;
            }
            return false;
        },
        pushScope(scope) {
            scopeStack.push(scope);
            return () => {
                const idx = scopeStack.lastIndexOf(scope);
                if (idx !== -1)
                    scopeStack.splice(idx, 1);
            };
        },
        activeScope() {
            return scopeStack[scopeStack.length - 1] ?? "editor";
        },
    };
};
//# sourceMappingURL=shortcuts.js.map